import { EyeIcon, EyeSlashIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import { type TextareaHTMLAttributes, useState } from 'react'
import { InputContainer, type InputContainerProps } from './input-container.tsx'
import * as styles from './text-area.css.ts'

/** Tall enough to show a pasted credential is more than one line, without dominating a drawer. */
const DEFAULT_ROWS = 6

/**
 * `children` and `externalClassName` are dropped from both halves of the intersection: neither is
 * destructured below, so either would otherwise reach the `<textarea>` through `...props` — the first
 * as children React refuses to render alongside `value`, the second as an unknown DOM attribute. Style
 * the wrapper through `className`, the way `TextInput` does.
 */
export type TextAreaProps = Omit<InputContainerProps, 'children' | 'externalClassName'> & {
  /** Current value of the textarea */
  value?: string
  /** Callback fired when the value changes */
  onChange?: (value: string) => unknown
  /** Aria-label attribute */
  ariaLabel?: string
  /**
   * Masks the value the way `<input type="password">` does, and adds a button to reveal it. A
   * textarea has no `type`, so this is CSS rather than browser behaviour. Also opts the field out of
   * the browser text services that a password input is exempt from by definition.
   */
  isMasked?: boolean
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value' | 'children'>

/**
 * A multi-line text field with the label, helper text and error treatment of the rest of the input
 * family. Use it over `TextInput` whenever the value may legitimately contain line breaks: a browser
 * strips them from an `<input>` value, so a pasted PEM or certificate arrives as a single line.
 */
export const TextArea: React.FC<TextAreaProps> = ({
  value = '',
  onChange = () => {},
  placeholder,
  label,
  id,
  name,
  ariaLabel,
  isRequired,
  explicitLabelCondition,
  helperText,
  errorText,
  isError,
  isMasked = false,
  rows = DEFAULT_ROWS,
  disabled,
  className: externalClassName,
  ...props
}) => {
  const [isRevealed, setIsRevealed] = useState(false)
  // The same test `InputContainer` applies to decide whether the label reads as an error, so a field
  // given only `errorText` gets a red box as well as a red label.
  const hasError = isError || !!errorText
  const isHidden = isMasked && !isRevealed

  return (
    <InputContainer
      externalClassName={externalClassName}
      label={label}
      id={id}
      isRequired={isRequired}
      explicitLabelCondition={explicitLabelCondition}
      helperText={helperText}
      errorText={errorText}
      isError={isError}
    >
      <div className={styles.textAreaContainer} data-disabled={disabled} data-error={hasError ? 'true' : undefined}>
        <textarea
          className={classNames(styles.textArea, { [styles.maskedTextArea]: isHidden })}
          id={id}
          name={name}
          value={value}
          rows={rows}
          placeholder={placeholder}
          disabled={disabled}
          // A masked field holds a credential, and the control this replaced was exempt from all of
          // these by virtue of being `type="password"`. A textarea is not: left at its defaults,
          // Chrome's enhanced spell check would send the pasted key to a remote service, and iOS
          // Safari would autocapitalise and autocorrect a typed one.
          spellCheck={isMasked ? false : undefined}
          autoCorrect={isMasked ? 'off' : undefined}
          autoCapitalize={isMasked ? 'off' : undefined}
          autoComplete={isMasked ? 'off' : undefined}
          aria-label={ariaLabel}
          aria-invalid={hasError ? true : undefined}
          onChange={event => onChange(event.target.value)}
          {...props}
        />
        {isMasked && (
          <button
            type="button"
            className={styles.revealButton}
            onClick={() => setIsRevealed(!isRevealed)}
            // Deliberately not named after the field. An accessible name containing the label makes
            // `getByLabelText(label, { exact: false })` — how the drawer suites reach a field — match
            // this button as well as the textarea. `TextInput`'s eye button is generic for the same
            // reason.
            aria-label={isRevealed ? 'Hide value' : 'Show value'}
            disabled={disabled}
          >
            {isRevealed ? <EyeIcon size={12} weight="fill" /> : <EyeSlashIcon size={12} weight="fill" />}
          </button>
        )}
      </div>
    </InputContainer>
  )
}
